import React, { useContext, useState } from 'react';

import { Link } from '@mui/material';

import { DataTable } from '../../../../common/components/DataTable';
import { GridView } from '../../../../common/components/GridView';
import { FlexRow, Spacer } from '../../../../common/components/v2';
import { DocumentType } from '../../../../common/constants/documentType';
import { FeatureFlags } from '../../../../common/constants/featureFlags';
import { Resource } from '../../../../common/constants/resource';
import { TaskState } from '../../../../common/constants/states';
import ElementLocators from '../../../../common/ElementLocators';
import { FeatureFlagsContext } from '../../../../common/featureFlagsWrapper';
import { ITask } from '../../../tasks/interfaces/ITask';
import { RxApplicationDetails } from '../../../tasks/types/rx';
import styles from '../../styles.module.scss';
import { factsExist } from '../../utilities/taskDetailUtilities';
import { RetryableEvidenceItem } from '../components/RetryableEvidenceItem';
import { RxApplicationSection } from './RxApplicantSection';
import { RxDetailsColumnResolver } from './RxDetailsColumnResolver';
import { RxDetailsColumns } from './RxDetailsColumns';
import { RxHistoryV2 } from './RxHistoryV2';
import { RxRulesColumnResolver } from './RxRulesColumnResolver';
import { RxRulesColumns } from './RxRulesColumns';
import { useRxSorting } from './useRxSorting';

const RxDetailLegacy = ({ rxDetail }: { rxDetail: RxApplicationDetails }) => {
  const { sortFields, sortFunction } = useRxSorting(rxDetail.rx || []);
  RxDetailsColumns.forEach(col => {
    if (sortFields.includes(col.name)) {
      col.fetchFn = sortFunction;
    }
  });

  const [showAllRules, setShowAllRules] = useState({
    isShowingAll: false,
  });
  const showMoreOrLess = () => {
    setShowAllRules({ isShowingAll: !showAllRules.isShowingAll });
  };

  return (
    <>
      {RxApplicationSection(rxDetail)}
      <Spacer height={16} />
      {rxDetail.rx.length ? (
        <DataTable
          rows={rxDetail.rx}
          columns={RxDetailsColumns}
          columnsResolver={RxDetailsColumnResolver}
          expandableRows={true}
          defaultSortDirection="desc"
          defaultSortKey="severity"
        />
      ) : null}

      {rxDetail.rxRules?.length ? (
        <>
          <Spacer height={16} />
          <FlexRow className={styles.TaskSubtitle}>
            <span>{Resource.RX_RULES}</span>
            <span className={styles.RightEnd}>
              Showing{' '}
              {showAllRules.isShowingAll
                ? rxDetail.rxRules.length
                : Math.min(
                    Resource.RX_RULE_INITIAL_PAGE_SIZE,
                    rxDetail.rxRules.length,
                  )}{' '}
              of {rxDetail.rxRules.length}
            </span>
          </FlexRow>

          <GridView
            rows={
              showAllRules.isShowingAll
                ? rxDetail.rxRules
                : rxDetail.rxRules.slice(0, Resource.RX_RULE_INITIAL_PAGE_SIZE)
            }
            columns={RxRulesColumns}
            columnsResolver={RxRulesColumnResolver}
            expandableRows={false}
          />
          <Spacer height={8} />
          {rxDetail.rxRules.length > Resource.RX_RULE_INITIAL_PAGE_SIZE ? (
            <FlexRow className={styles.ShowMoreLess}>
              <Link onClick={showMoreOrLess}>
                {showAllRules.isShowingAll
                  ? Resource.RX_RULE_SHOW_LESS
                  : Resource.RX_RULE_SHOW_ALL}
              </Link>
            </FlexRow>
          ) : null}
        </>
      ) : null}
    </>
  );
};

export const RxDetail = ({
  task,
  retryHandler,
  markOutdatedHandler,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  deleteHandler,
}: {
  task: ITask;
  retryHandler: (taskId: number) => void;
  markOutdatedHandler: (taskId: number) => void;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
}) => {
  const { evidence } = task;
  const { flags } = useContext(FeatureFlagsContext);
  const isRxHistoryV2Enabled =
    !!flags[FeatureFlags.UW_NORA_RX_HISTORY_V2_ENABLED];

  const rxDetail: RxApplicationDetails | null = evidence
    ? (evidence.facts as RxApplicationDetails)
    : null;

  const content =
    factsExist(evidence) && rxDetail ? (
      isRxHistoryV2Enabled ? (
        <>
          {RxApplicationSection(rxDetail)}
          <Spacer height={16} />
          <RxHistoryV2 rxDetail={rxDetail} />
        </>
      ) : (
        <RxDetailLegacy rxDetail={rxDetail} />
      )
    ) : (
      <></>
    );

  return (
    <div data-label={ElementLocators.LOCATOR_RX_HISTORY_SECTION}>
      <RetryableEvidenceItem
        task={task}
        documentType={DocumentType.RX}
        title={Resource.TASK_TITLE_RX}
        downloadHandler={downloadHandler}
        uploadHandler={uploadHandler}
        deleteHandler={deleteHandler}
        retryHandler={retryHandler}
        taskStateHandler={taskStateHandler}
        markOutdatedHandler={markOutdatedHandler}
      >
        {content}
      </RetryableEvidenceItem>
    </div>
  );
};
